import React, { useEffect, useRef } from "react";
import { PanelGroup } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import { MoveTypes } from "../../../api/Inventory/Entities/InventoryMovementEntities";
import useInventoryCart from "../Hooks/useInventoryCart";
import ProtocolItemField from "./ProtocolItemField";
import ProtocolReturnEntryPanel from "./ProtocolReturnEntryPanel";
import ProtocolReturnEntryForm from "./ProtocolReturnEntryForm";

export default function ProtocolReturnMovement({
    entries, comprobantes, remaining, assignment, destination, persistedMove,
    disabled, onMoveChange, onUserEdit, onAddEntry, onRemoveEntry, returnedItems, lockedOrigins,
    onReDispenseReturned, onReDispenseEntry
}) {
    const { t } = useTranslation();
    const [move, , cart] = useInventoryCart(
        persistedMove?.id ?? 0, MoveTypes.ProtocolReturn.Name,
        assignment.assignmentType, assignment.assignId, destination, persistedMove
    );

    const entryToUniqueId = useRef(new Map());

    const mapSeeded = useRef(false);
    useEffect(() => {
        if (mapSeeded.current) return;
        if ((move?.items?.length ?? 0) === 0) return;
        if (entries.length === 0) return;
        mapSeeded.current = true;

        const pool = new Map();
        for (const item of move.items) {
            const list = pool.get(item.originMoveItemId) ?? [];
            list.push(item);
            pool.set(item.originMoveItemId, list);
        }
        for (const entry of entries) {
            if (!(entry.returned > 0)) continue;
            const candidates = pool.get(entry.originMoveItemId) ?? [];
            const item = candidates.shift();
            if (item != null) entryToUniqueId.current.set(entry.entryKey, item.uniqueId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [move, entries]);

    useEffect(() => {
        if (move?.userId == null) return;
        if (persistedMove != null && !mapSeeded.current && (move?.items?.length ?? 0) > 0) return;

        for (const entry of entries) {
            if (!(entry.returned > 0) || entryToUniqueId.current.has(entry.entryKey)) continue;
            const uniqueId = cart.AddItem(
                entry.item, entry.release, 1, entry.assignment, entry.perUnitTotal, null,
                entry.originMoveItemId, entry.returned, entry.releaseTargetState ?? null,
                entry.condition ?? null, entry.dispensedKitQuantity ?? null
            );
            entryToUniqueId.current.set(entry.entryKey, uniqueId);
        }

        const live = new Set(entries.map(e => e.entryKey));
        for (const [entryKey, uniqueId] of Array.from(entryToUniqueId.current)) {
            if (live.has(entryKey)) continue;
            cart.RemoveItem({ uniqueId });
            entryToUniqueId.current.delete(entryKey);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries, move]);

    useEffect(() => {
        if (move?.userId == null) return;
        onMoveChange(move);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [move]);

    const hasAddableItems = comprobantes.some(c =>
        c.items.some(l => (remaining?.get(l.originMoveItemId) ?? 0) > 0));

    if (disabled && entries.length === 0 && returnedItems.length === 0) return null;

    return (
        <PanelGroup accordion id="protocol-return-entries" defaultActiveKey="return-entry-form">
            {returnedItems.map((item, index) => {
                const stillReDispensed = lockedOrigins?.has(item.originMoveItemId) ?? false;
                return (
                    <ProtocolItemField key={`returned-${item.uniqueId ?? index}`} index={`returned-${index}`} movementItem={item}
                        disabled={true} locked={stillReDispensed} assignmentType={assignment.assignmentType}
                        assignId={assignment.assignId} hasActions={!disabled} actionsDisbled={disabled || stillReDispensed}
                        handleRemove={(cartItem) => {
                            if (onUserEdit) onUserEdit();
                            cart.RemoveItem({ uniqueId: cartItem?.uniqueId });
                        }}
                        handleReDispense={!disabled && !stillReDispensed && item.releaseTargetState !== 'Missed' ? onReDispenseReturned : null}
                        isDispensed={true} />
                );
            })}
            {entries.map((entry, index) => (
                <ProtocolReturnEntryPanel key={entry.entryKey} entry={entry} index={index}
                    disabled={disabled} onRemove={() => onRemoveEntry(entry.entryKey)}
                    onReDispense={() => onReDispenseEntry(entry.entryKey)} />
            ))}
            {!disabled && (hasAddableItems
                ? <ProtocolReturnEntryForm
                    comprobantes={comprobantes} remaining={remaining}
                    disabled={disabled} onAdd={onAddEntry} />
                : <div className="alert alert-info protocol-return__empty-alert" role="alert">
                    {t('inventory.commons.noReturnableItems')}
                </div>
            )}
        </PanelGroup>
    );
}
